import * as React from 'react';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import * as Yup from 'yup';
import TickIcon from '../../../../components/icons/TickIcon';
import type IDiscountCodeState from '../../../../interfaces/DiscountCodeState';
import { selectDiscount } from '../../../discount/discountSlice';
import urlUtils from '../../../../utils/urlUtils';
import './DiscountCode.scss';

interface DiscountCodeFormValues {
  discountCode: string;
}

const DiscountCodeSchema = Yup.object().shape({
  discountCode: Yup.string().label('Discount code').required(),
});

const DiscountCodeForm = () => {
  const dispatch = useDispatch();

  const promoCodeFromUrl = urlUtils.getUrlParameterByName('promo') || '';

  const initialValues: DiscountCodeFormValues = {
    discountCode: promoCodeFromUrl,
  };

  const handleSubmit = ({ discountCode }) => {
    dispatch({ type: 'discount/reset' });
    dispatch({
      type: 'discount/applyDiscount',
      payload: {
        discountCode,
      },
    });
  };

  const handleRemoveDiscount = (resetForm) => () => {
    dispatch({ type: 'discount/reset' });
    dispatch({ type: 'railcards/setDiscounts', payload: { discounts: null } });
    resetForm();
  };

  const { error: discountCodeError, success } =
    useSelector<IDiscountCodeState, IDiscountCodeState>(selectDiscount) || {};

  React.useEffect(() => {
    dispatch({ type: 'discount/reset' });

    if (promoCodeFromUrl) {
      dispatch({
        type: 'discount/applyDiscount',
        payload: {
          discountCode: promoCodeFromUrl,
        },
      });
    }
  }, [dispatch, promoCodeFromUrl]);

  return (
    <div className="discount-form">
      <Formik
        initialValues={initialValues}

        validationSchema={DiscountCodeSchema}

        onSubmit={(values, { setSubmitting }) => {
          setTimeout(() => {
            setSubmitting(false);
          }, 400);
          handleSubmit(values);
        }}
      >
        {({ values, handleChange, handleBlur, handleSubmit, isSubmitting, resetForm }) => {
          const isDisabled = isSubmitting || !values.discountCode;

          return (
            <Form className="discount-form" onSubmit={handleSubmit}>
              <React.Fragment>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="discountCode">
                      <Form.Label>Discount code</Form.Label>
                      <div className="position-relative">
                        <Form.Control
                          name="discountCode"
                          type="discountCode"
                          placeholder=""
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.discountCode}
                          data-testid="discount-code"
                          isInvalid={Boolean(discountCodeError)}
                        />
                        {success && (
                          <TickIcon
                            className="position-absolute"
                            fill="#367C23"
                            style={{ width: 32, height: 32, top: 4, right: 4 }}
                            data-testid="tick-success"
                          />
                        )}
                      </div>
                      {discountCodeError && (
                        <div className="form-text">
                          <p data-testid="invalid-code">{discountCodeError}</p>
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col>
                    {success ? (
                      <button
                        type="button"
                        className="discount-remove"
                        onClick={handleRemoveDiscount(resetForm)}
                      >
                        Remove
                      </button>
                    ) : (
                      <Button
                        type="submit"
                        disabled={isDisabled}
                        className="discount-form__apply"
                        variant="primary"
                      >
                        Apply
                      </Button>
                    )}
                  </Col>
                </Row>
              </React.Fragment>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default DiscountCodeForm;
